import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import api, { formatError } from "@/lib/api";
import logoWhite from "@/assets/logo-white.png";
import { fmtEuro } from "@/lib/status";
import { Button } from "@/components/ui/button";

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const load = useCallback(() => {
    api.get(`/public/quotes/${token}`).then((r) => setData(r.data))
      .catch((e) => setError(formatError(e.response?.data?.detail)));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const decide = async (kind) => {
    try {
      await api.post(`/public/quotes/${token}/${kind}`, { customer_name: "" });
      setDone(kind); toast.success(kind === "approve" ? "Preventivo accettato" : "Preventivo rifiutato");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white grain-panel">
      <header className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-6">
        <img src={logoWhite} alt="Dexter Lab" className="h-8 w-auto object-contain" />
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Preventivo riparazione</div>
      </header>

      <main className="mx-auto max-w-lg px-6 pb-20">
        {error && <div className="mt-10 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>}
        {data && (
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur" data-testid="public-quote">
            <div className="font-mono text-sm text-primary">{data.repair_number} · {data.quote_number}</div>
            <h1 className="mt-1 font-heading text-2xl font-extrabold">{data.device.brand} {data.device.model}</h1>
            {data.problem && <p className="mt-2 text-sm text-slate-400">{data.problem}</p>}

            <table className="mt-6 w-full text-sm">
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-2">{it.description}</td>
                    <td className="py-2 text-right text-slate-400">{it.quantity}×</td>
                    <td className="py-2 text-right">{fmtEuro(it.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 space-y-1 text-right text-sm text-slate-400">
              {data.discount > 0 && <div>Sconto: -{fmtEuro(data.discount)}</div>}
              <div>IVA {data.tax_rate}%: {fmtEuro(data.tax)}</div>
              <div className="font-heading text-2xl font-extrabold text-white">Totale: {fmtEuro(data.total)}</div>
              <div className="text-xs text-slate-500">Validità fino al {data.valid_until}</div>
            </div>

            {data.status === "SENT" && !done && (
              <div className="mt-8 grid grid-cols-2 gap-3">
                <Button data-testid="approve-quote-btn" onClick={() => decide("approve")} className="bg-emerald-500 hover:bg-emerald-600">
                  <Check className="mr-2 h-4 w-4" /> Accetto
                </Button>
                <Button data-testid="reject-quote-btn" variant="outline" onClick={() => decide("reject")}
                  className="border-rose-500/40 bg-transparent text-rose-300 hover:bg-rose-500/10">
                  <X className="mr-2 h-4 w-4" /> Rifiuto
                </Button>
              </div>
            )}
            {data.status === "APPROVED" && <div className="mt-6 rounded-lg bg-emerald-500/20 px-4 py-3 text-center text-sm font-semibold text-emerald-300">Preventivo approvato ✓</div>}
            {data.status === "REJECTED" && <div className="mt-6 rounded-lg bg-rose-500/20 px-4 py-3 text-center text-sm font-semibold text-rose-300">Preventivo rifiutato</div>}
          </div>
        )}
      </main>
    </div>
  );
}
